import { useState, useContext } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Link } from "react-router-dom";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Tag,
  Flex,
  Label,
  Table,
  Button,
  TableRow,
  useModal,
  NoResults,
  TableBody,
  TableCell,
  TableHead,
  TextInput,
  FilterMenu,
  Pagination,
  PageWrapper,
  PanelSlider,
  StretchPanel,
  TableHeadCell,
  useFilterReducer,
  NotificationContext,
  usePaginationReducer,
} from "@ubisend/pulse-components";

import {
  Deploy,
  TagSelect,
  ProjectInfo,
  DeployStatus,
  ProjectStatus,
  ProjectSettings,
  PermissionFilter,
} from "../Components";
import {
  updateProjectSettings,
  deployProject as deployProjectApi,
} from "../api/projects";

dayjs.extend(relativeTime);

const Projects = () => {
  const [showProjectInfo, setShowProjectInfo] = useState();
  const [editSettings, setEditSettings] = useState(false);
  const [showDeployments, setShowDeployments] = useState(false);

  const { showSuccess } = useContext(NotificationContext);
  const { showModal, hideModal } = useModal();
  const queryClient = useQueryClient();
  const pagination = usePaginationReducer({ id: "projects" });

  const filters = useFilterReducer({
    id: "projects",
    initialFilters: { name: "", groups: [2] },
    options: { pagination },
  });

  const query = useQuery({
    queryKey: ["projects", { ...pagination.params, ...filters.form }],
  });

  const { mutate: deployProject } = useMutation({
    mutationFn: deployProjectApi,
  });

  const handleDeploy = (project) => {
    showModal({
      header: "Deploy Project",
      body: `Are you sure you want to deploy ${project.name}?`,
      handleConfirm: async () => {
        try {
          const response = await deployProject({ id: project.project_id });

          if (response?.status === 200) {
            showSuccess(`Successfully deployed project`);
            queryClient.invalidateQueries({ queryKey: ["projects"] });
          }
        } finally {
          hideModal();
        }
      },
    });
  };

  const { mutate: updateSettings } = useMutation({
    mutationFn: updateProjectSettings,
    onSuccess: () => {
      showSuccess(`Successfully updated settings`);
      setEditSettings(false);
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  const handleProjectSettingSubmit = (settings) => updateSettings({ settings });

  const handleGroupsChange = (groups) => {
    filters.updateFilters({
      groups: groups ? groups.map((group) => group.value) : [],
    });
  };

  const handleNameChange = (event) => {
    const name = event.target.value;
    filters.updateFilters({ name });
  };

  const replaceUrl = (url) => {
    return "https://" + url.replace(/^https?:\/\//, "");
  };

  return (
    <PageWrapper
      header={"All Pulse Projects"}
      subheader={
        query.dataUpdatedAt
          ? `Last updated: ${dayjs(query.dataUpdatedAt).from(dayjs())}`
          : ""
      }
      actions={
        <Flex xSpace>
          <FilterMenu
            buttonProps={{ loading: query.isLoading }}
            {...filters.props}
          >
            <Flex style={{ width: "20rem" }} col>
              <Flex col fat mb>
                <Label htmlFor="name">Name</Label>
                <TextInput
                  id="name"
                  placeholder="Installation name"
                  value={filters.filters.name}
                  onChange={handleNameChange}
                />
              </Flex>
              <Flex col fat>
                <Label htmlFor="tags">Tags</Label>
                <TagSelect
                  id="tags"
                  placeholder="Filter installs by tag"
                  for="installs"
                  value={filters.filters.groups}
                  onChange={handleGroupsChange}
                />
              </Flex>
            </Flex>
          </FilterMenu>
          <PermissionFilter permission="edit deployments">
            <Button as={Link} icon="plus" to={"/projects/create"}>
              Create Project
            </Button>
          </PermissionFilter>
          <PermissionFilter permission="edit deployments">
            <Button
              icon="airplane"
              onClick={() => setShowDeployments(true)}
              variant="secondary"
            >
              Deploy
            </Button>
          </PermissionFilter>
        </Flex>
      }
    >
      {showDeployments && (
        <Deploy
          projects={query.isSuccess && query.data.data}
          handleCancel={() => setShowDeployments(false)}
        />
      )}
      {query.isSuccess && query.data.data.length === 0 && (
        <NoResults
          text="No projects found"
          subtitle="Why not create a shiny new one"
        />
      )}
      {query.isSuccess && query.data.data.length > 0 && (
        <StretchPanel>
          <Table loading={query.isLoading} loadingColumns={4}>
            <TableHead>
              <TableRow>
                <TableHeadCell>Client Name</TableHeadCell>
                <TableHeadCell>App Url</TableHeadCell>
                <TableHeadCell>Tags</TableHeadCell>
                <TableHeadCell>Current State</TableHeadCell>
                <TableHeadCell>Current Status</TableHeadCell>
                <TableHeadCell />
              </TableRow>
            </TableHead>
            {!query.isLoading && query.data?.data.length > 0 && (
              <TableBody>
                {query.data.data.map((project, key) => (
                  <TableRow key={"project_" + key}>
                    <TableCell>{project.name}</TableCell>
                    <TableCell>
                      <a
                        target="blank"
                        rel="noreferrer"
                        style={{ textDecoration: "none", color: "#684b99" }}
                        href={replaceUrl(project.app_url)}
                      >
                        {replaceUrl(project.app_url)}
                      </a>
                    </TableCell>
                    <TableCell>
                      {project.tags.length > 0 && (
                        <Flex fat xSpaceSm>
                          {project.tags.map((tag) => (
                            <Tag key={"tag_" + tag.id}>{tag.name}</Tag>
                          ))}
                        </Flex>
                      )}
                    </TableCell>
                    <TableCell>
                      <DeployStatus value={project.state.name} />
                    </TableCell>
                    <TableCell>
                      <ProjectStatus enabled={project.enabled} />
                    </TableCell>
                    <TableCell>
                      <Flex right xSpace>
                        <PermissionFilter permission="edit deployments">
                          <Button
                            aria-label={`deploy-button-${key}`}
                            disabled={project.state.identifier === "deploying"}
                            icon="airplane"
                            onClick={() => handleDeploy(project)}
                            variant="secondary"
                          >
                            Deploy
                          </Button>
                        </PermissionFilter>
                        <PermissionFilter permission="edit deployments">
                          <Button
                            as={Link}
                            disabled={
                              !project.client_secret || !project.client_id
                            }
                            icon="key"
                            to={{
                              pathname:
                                !project.client_secret || !project.client_id
                                  ? "/"
                                  : `/features/${project.project_id}`,
                              state: { name: project.name, project },
                            }}
                          >
                            Features
                          </Button>
                        </PermissionFilter>
                        <PermissionFilter permission="edit deployments">
                          <Button
                            as={Link}
                            disabled={
                              !project.client_secret || !project.client_id
                            }
                            icon="key"
                            to={{
                              pathname:
                                !project.client_secret || !project.client_id
                                  ? "/"
                                  : `/licenses/${project.project_id}`,
                              state: { name: project.name },
                            }}
                          >
                            Licenses
                          </Button>
                        </PermissionFilter>
                        <PermissionFilter permission="edit deployments">
                          <Button
                            aria-label={`settings-button-${key}`}
                            disabled={project.state.identifier !== "deployed"}
                            icon="cog"
                            onClick={() => setEditSettings(project)}
                            variant="secondary"
                          >
                            Settings
                          </Button>
                        </PermissionFilter>
                        <Button
                          aria-label={`info-button-${key}`}
                          colour="primary"
                          icon="eye"
                          onClick={() => setShowProjectInfo(project)}
                        >
                          Info
                        </Button>
                      </Flex>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
          {query.data?.meta && (
            <Pagination pagination={query.data.meta} {...pagination.props} />
          )}
        </StretchPanel>
      )}

      {showProjectInfo && (
        <PanelSlider
          from="right"
          header={showProjectInfo.name}
          handleHide={() => setShowProjectInfo(null)}
        >
          <Flex mt col>
            <ProjectInfo project={showProjectInfo} />
          </Flex>
        </PanelSlider>
      )}
      {editSettings !== false && (
        <PanelSlider
          header={"Edit Project Settings"}
          handleHide={() => setEditSettings(false)}
        >
          <Flex mt col>
            <ProjectSettings
              project={editSettings}
              handleCancel={() => setEditSettings(false)}
              submit={handleProjectSettingSubmit}
            />
          </Flex>
        </PanelSlider>
      )}
    </PageWrapper>
  );
};

export default Projects;
